import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import {
  PlusIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

// The Daily-checklist editor (Me page). Pick which items appear in the Log-page
// queue, reorder them (order = the order you're prompted in), remove them, and
// add your own free-text items. The list here IS the list you see when logging.
//
// Saved routines are addable here too. This is the page people reach for when they
// want "a workout on my checklist", and without a routine section the only thing on
// offer was "Add your own" — which makes a dead tap-only row with nothing in it. So
// routines get their own section (pin an existing one; a pointer to the Log page to
// build the first), and a custom item is described as what it actually is.

const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'item';

// `refreshKey` / `onChanged` pair this with the RoutineManager below it — see the note
// there. Creating a routine down there must make it appear in "Add a routine" up here.
const ChecklistEditor = ({ refreshKey = 0, onChanged }) => {
  const [items, setItems] = useState([]);
  const [saved, setSaved] = useState([]);       // last-persisted snapshot
  const [catalog, setCatalog] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [customLabel, setCustomLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/checklist/config');
      setItems(res.data.items || []);
      setSaved(res.data.items || []);
      setCatalog(res.data.catalog || []);
    } catch {
      // Non-fatal — the section just doesn't render if the fetch fails.
    } finally {
      setLoading(false);
    }
    try {
      const res = await apiClient.get('/api/templates');
      setRoutines(res.data || []);
    } catch {
      setRoutines([]);   // no routine section rather than a broken one
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const dirty = JSON.stringify(items) !== JSON.stringify(saved);
  const available = catalog.filter((c) => !items.some((i) => i.id === c.id));
  const unpinnedRoutines = routines.filter(
    (t) => !items.some((i) => i.kind === 'template' && i.template_id === t.id),
  );

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    setItems((prev) => {
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const remove = (id) => setItems((prev) => prev.filter((i) => i.id !== id));

  const addCatalog = (c) => setItems((prev) => [...prev, { ...c, enabled: true }]);

  // Pin a saved routine as a one-tap "Log" row. Same shape the star toggle in the
  // routine picker writes, so the two entry points stay interchangeable.
  const addRoutine = (t) => setItems((prev) => [...prev, {
    id: `template_${t.id}`, label: t.name, kind: 'template',
    template_id: t.id, meta: '', auto: false, enabled: true,
  }]);

  const addCustom = () => {
    const label = customLabel.trim();
    if (!label) return;
    const existing = new Set(items.map((i) => i.id));
    const base = 'custom-' + slugify(label);
    let id = base;
    let n = 2;
    while (existing.has(id)) id = `${base}-${n++}`;
    setItems((prev) => [...prev, {
      id, label, meta: 'tap when done', kind: 'custom', auto: false, enabled: true,
    }]);
    setCustomLabel('');
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiClient.put('/api/checklist/config', { items });
      setItems(res.data.items || []);
      setSaved(res.data.items || []);
      toast.success('Checklist saved.');
      if (onChanged) onChanged();   // pins changed → the star states below are stale
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Couldn’t save your checklist.');
    } finally {
      setSaving(false);
    }
  };

  const badge = (item) =>
    item.auto ? 'Auto'
      : item.kind === 'template' ? 'Routine'
        : item.kind === 'custom' ? 'Custom' : 'Logged';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Daily checklist</CardTitle>
      </CardHeader>
      <CardContent className="p-6 pt-0 space-y-5">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Choose what shows in your logging checklist and the order you’re prompted in.
          Most items tick themselves as you log; add your own for anything else.
        </p>

        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        ) : (
          <>
            <div className="space-y-2">
              {items.map((item, i) => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 rounded-lg border border-surface-border dark:border-surface-dark-border px-3 py-2"
                >
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      aria-label="Move up"
                      className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30"
                    >
                      <ChevronUpIcon className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(i, 1)}
                      disabled={i === items.length - 1}
                      aria-label="Move down"
                      className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30"
                    >
                      <ChevronDownIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-medium text-gray-900 dark:text-white truncate">
                      {item.label}
                    </p>
                    {item.meta && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{item.meta}</p>
                    )}
                  </div>
                  <span className="shrink-0 text-sm font-semibold uppercase tracking-wide rounded-full border border-surface-border dark:border-surface-dark-border px-2 py-0.5 text-gray-500 dark:text-gray-400">
                    {badge(item)}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(item.id)}
                    title="Remove from list"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {items.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Your checklist is empty — add something below.
                </p>
              )}
            </div>

            {available.length > 0 && (
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Add from your metrics
                </span>
                <div className="flex flex-wrap gap-2">
                  {available.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => addCatalog(c)}
                      className="inline-flex items-center gap-1 rounded-full border border-surface-border dark:border-surface-dark-border px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      <PlusIcon className="w-3.5 h-3.5" /> {c.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Routines — a workout or stack you've saved, pinned as a one-tap row. */}
            <div className="space-y-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Add a routine
              </span>
              {unpinnedRoutines.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {unpinnedRoutines.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => addRoutine(t)}
                      className="inline-flex items-center gap-1 rounded-full border border-surface-border dark:border-surface-dark-border px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      <PlusIcon className="w-3.5 h-3.5" />
                      {t.icon ? `${t.icon} ` : ''}{t.name}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {routines.length === 0
                    ? 'No saved routines yet.'
                    : 'All your routines are already on the list.'}
                </p>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400">
                A routine is a saved bundle — a workout, a supplement stack, a usual meal.
                Build one under <span className="font-medium">Your routines</span> just
                below, then pin it here to log it in one tap, or just say its name while
                logging.
              </p>
            </div>

            <div className="space-y-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Add your own
              </span>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                A plain reminder you tick by hand — it holds no data. For anything you want
                actually logged, add a routine above instead.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
                  maxLength={60}
                  placeholder="e.g. Meditated"
                  className="flex-1 h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-3 text-sm text-gray-900 dark:text-gray-100"
                />
                <Button type="button" variant="outline" onClick={addCustom} disabled={!customLabel.trim()}>
                  <PlusIcon className="w-4 h-4 mr-1.5" /> Add
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-1">
              <Button type="button" onClick={save} disabled={!dirty || saving}>
                {saving ? 'Saving…' : 'Save checklist'}
              </Button>
              {dirty && (
                <Button type="button" variant="ghost" onClick={() => setItems(saved)} disabled={saving}>
                  Discard changes
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ChecklistEditor;
